// The city's own names on the disc, and the one thing V4 changes about them:
// they are SOLVED on settle and merely TRANSLATED in between (SPEC-viewfinder
// §4.3).
//
// Until now `paintDistricts` ran from `reproject()`, which a `requestAnimation
// Frame` drives on every `move` frame — so every frame rebuilt up to five
// elements and measured each of them against up to five anchors and every mark
// on the disc. It is one of the two per-frame offenders the prototype's own
// diagnosis named, and it is a layout solve, not arithmetic: the cost is the
// `getBoundingClientRect` calls, not the geometry.
//
// The promise it makes is unchanged and is still asserted — „a centre move
// never leaves a district name cut by the block's edge". What changes is WHEN
// the solve happens: the placement is settled once, the offset it produced is
// remembered on the element, and every frame of the move adds that offset to
// the fresh projection. The name keeps its distance from the ground it names,
// which is what the promise is about; a name that would newly collide with a
// mark is re-solved on the next settle, one rebuild later.
import type { ViewState } from '../state/viewstate.ts';
import type { Project } from './beyond.ts';
import { districtsFor } from './districts.ts';
import { placeDistrict } from './marks-dom.ts';

/** Everything the solve measures, all of it the wheel's own furniture. */
export type DistrictDeps = {
  layer: HTMLElement;
  overlay: HTMLElement;
  marks: HTMLElement;
  centreMark: HTMLElement;
  project: Project;
};

/**
 * Up to five district names, nearest the centre first, never under a 2 km
 * radius and never past 0.7 of it (`districtsFor`). Drawn after the marks so a
 * name that lands on one can be dropped: the mark is the fact and the name is
 * scenery, so the mark wins the space. Names are kept off each other too,
 * nearest first — „WILDA" over „ŁĘGI DĘBIŃSKIE" reads as neither.
 */
export function solveDistricts(
  deps: DistrictDeps,
  view: ViewState,
  centre: { lat: number; lon: number },
  dial: boolean,
): void {
  const { layer, overlay, marks, centreMark, project } = deps;
  layer.replaceChildren();
  if (dial) return;
  const block = overlay.getBoundingClientRect();
  // One rule: a district name gives way to anything that says WHERE or WHAT,
  // and takes precedence over nothing. So it must not land on a mark a resident
  // can open or on the centre of the circle — „STARE M●IASTO" is what leaving
  // the centre out looks like — and where it collides it is dropped, because
  // the kreska already names the place in words.
  //
  // Three things are deliberately NOT obstacles, and by the same rule: the 7 px
  // context dots say neither where nor what (at 5 km there are twenty, and
  // letting each veto a name left the disc with one); a ring label is a
  // distance rather than a place, and ring labels stack up the disc's vertical
  // centre line, exactly where the centre's own district sits; and §3's dots
  // beyond the ring, which are scenery under the names themselves.
  const taken = [...marks.querySelectorAll('button')].map((el) => el.getBoundingClientRect());
  if (centreMark.offsetParent !== null) taken.push(centreMark.getBoundingClientRect());

  for (const district of districtsFor(centre, view.radius)) {
    const at = project(district.lon, district.lat);
    const el = document.createElement('span');
    el.className = 'circle__district';
    el.textContent = district.name;
    el.dataset.lon = String(district.lon);
    el.dataset.lat = String(district.lat);
    layer.append(el);

    // The whole point of drawing these ourselves: the edge never cuts a name.
    // „GRUNWALD" reading „ALD" orients nobody. Unlike MapLibre we can measure
    // the box against the block, and a name that does not fit is pulled toward
    // the centre until it does rather than dropped — it still names the ground
    // it sits over, and the alternative is a disc with no names.
    //
    // A name that lands on a mark is not dropped either until it has tried its
    // other anchors: 1,1 × its own height above or below it, and 1,1 × its own
    // width to either side — a step past the box it was refused, so the name
    // clears what it collided with rather than shaving it.
    const label = placeDistrict(el, at, block, taken);
    if (!label) {
      el.remove();
      continue;
    }
    // A name that stayed is something the next name must not land on either.
    taken.push(label);
    // And what the solve DECIDED, kept as the one number a frame needs: how far
    // the name was moved off the ground it names. `placeDistrict` writes the
    // element's transform, so the offset is read back off the box rather than
    // returned — the box is the only place the pull and the anchor are both in.
    el.dataset.offx = String(label.left + label.width / 2 - block.left - at.x);
    el.dataset.offy = String(label.top + label.height / 2 - block.top - at.y);
  }
}

/**
 * A frame's worth of district names: one `project` and one transform each, and
 * nothing measured (§4.3). The offset is the solve's, kept on the element.
 */
export function translateDistricts(layer: HTMLElement, project: Project): void {
  for (const el of layer.children as HTMLCollectionOf<HTMLElement>) {
    const lon = Number(el.dataset.lon);
    const lat = Number(el.dataset.lat);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    const at = project(lon, lat);
    const x = at.x + (Number(el.dataset.offx) || 0);
    const y = at.y + (Number(el.dataset.offy) || 0);
    // The same shape `pullInside` writes, so the two never disagree about what
    // a district's transform means.
    el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  }
}
